import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

const STATUS_COLORS = {
  'Pending': 'bg-blue-100 text-blue-800',
  'In Progress': 'bg-yellow-100 text-yellow-800',
  'Order Email Received': 'bg-purple-100 text-purple-800',
  'Order Intake': 'bg-blue-100 text-blue-800',
  'Order Processing': 'bg-indigo-100 text-indigo-800',
  'Legal Tech': 'bg-cyan-100 text-cyan-800',
  'Production Front': 'bg-yellow-100 text-yellow-800',
  'Invoicing': 'bg-orange-100 text-orange-800',
  'Production Back': 'bg-amber-100 text-amber-800',
  'Completed': 'bg-green-100 text-green-800',
  'On Hold': 'bg-orange-100 text-orange-800',
  'Cancelled': 'bg-red-100 text-red-800',
};

const PIPELINE_STAGES = [
  { key: 'Order Email Received', short: 'Email Rcvd', color: 'bg-purple-500', light: 'bg-purple-50 border-purple-200 hover:bg-purple-100' },
  { key: 'Order Intake', short: 'Intake', color: 'bg-blue-500', light: 'bg-blue-50 border-blue-200 hover:bg-blue-100' },
  { key: 'Order Processing', short: 'Processing', color: 'bg-indigo-500', light: 'bg-indigo-50 border-indigo-200 hover:bg-indigo-100' },
  { key: 'Legal Tech', short: 'Legal Tech', color: 'bg-cyan-500', light: 'bg-cyan-50 border-cyan-200 hover:bg-cyan-100' },
  { key: 'Production Front', short: 'Prod Front', color: 'bg-yellow-500', light: 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100' },
  { key: 'Invoicing', short: 'Invoicing', color: 'bg-orange-500', light: 'bg-orange-50 border-orange-200 hover:bg-orange-100' },
  { key: 'Production Back', short: 'Prod Back', color: 'bg-amber-600', light: 'bg-amber-50 border-amber-200 hover:bg-amber-100' },
  { key: 'Completed', short: 'Completed', color: 'bg-green-500', light: 'bg-green-50 border-green-200 hover:bg-green-100' },
];

function DashboardHome({ user }) {
  const [stats, setStats] = useState(null);
  const [selectedStage, setSelectedStage] = useState(null);
  const [stageOrders, setStageOrders] = useState([]);
  const [stageLoading, setStageLoading] = useState(false);
  const token = localStorage.getItem('token');
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/stats`, { headers: { Authorization: `Bearer ${token}` } });
        setStats(res.data);
      } catch (err) { console.error('Failed to load stats', err); }
    };
    load();
  }, [token]);

  const openStage = async (stage) => {
    setSelectedStage(stage);
    setStageLoading(true);
    try {
      const url = stage.key === '__all__'
        ? `${API_URL}/api/work-orders`
        : `${API_URL}/api/work-orders?status=${encodeURIComponent(stage.key)}`;
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setStageOrders(res.data.slice(0, 200));
    } catch (e) { setStageOrders([]); }
    setStageLoading(false);
  };

  const totalWOs = stats?.total_wos ?? 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Welcome back, {user.full_name}</h1>
          <p className="text-gray-500 text-sm">Discovery Records Management System</p>
        </div>
        <div className="text-right">
          <div className="text-xs text-gray-400">This Month</div>
          <div className="text-xl font-bold text-blue-600">${stats?.month_invoice_total?.toFixed(2) ?? '0.00'}</div>
          <div className="text-xs text-gray-400">in invoices</div>
        </div>
      </div>

      {/* Pipeline flow */}
      <div className="mb-6">
        <div className="flex items-center gap-0 overflow-x-auto pb-2">
          {PIPELINE_STAGES.map((stage, idx) => {
            const count = stats?.stage_counts?.[stage.key] ?? 0;
            const isLast = idx === PIPELINE_STAGES.length - 1;
            return (
              <div key={stage.key} className="flex items-center flex-shrink-0">
                {/* Stage card */}
                <div
                  onClick={() => openStage(stage)}
                  className={`relative border-2 rounded-xl p-3 w-28 cursor-pointer transition-all shadow-sm ${stage.light} ${selectedStage?.key === stage.key ? 'ring-2 ring-offset-1 ring-gray-400 scale-105' : ''}`}
                >
                  <div className={`w-2 h-2 rounded-full ${stage.color} mb-1.5`} />
                  <div className="text-xs font-semibold text-gray-700 leading-tight mb-1">{stage.short}</div>
                  <div className="text-2xl font-bold text-gray-900">{count}</div>
                  <div className="text-xs text-gray-400">orders</div>
                </div>
                {/* Arrow between cards */}
                {!isLast && (
                  <div className="flex items-center px-1 text-gray-300 flex-shrink-0">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                )}
              </div>
            );
          })}
          {/* Total Orders card */}
          <div className="flex items-center flex-shrink-0">
            <div className="flex items-center px-1 text-gray-300">
              <div className="w-6 border-t-2 border-dashed border-gray-300" />
            </div>
            <div
              onClick={() => openStage({ key: '__all__', short: 'All Orders', color: 'bg-gray-500', light: 'bg-gray-50 border-gray-200 hover:bg-gray-100' })}
              className={`border-2 rounded-xl p-3 w-28 cursor-pointer transition-all shadow-sm bg-gray-50 border-gray-200 hover:bg-gray-100 ${selectedStage?.key === '__all__' ? 'ring-2 ring-offset-1 ring-gray-400 scale-105' : ''}`}
            >
              <div className="w-2 h-2 rounded-full bg-gray-500 mb-1.5" />
              <div className="text-xs font-semibold text-gray-700 leading-tight mb-1">Total</div>
              <div className="text-2xl font-bold text-gray-900">{totalWOs}</div>
              <div className="text-xs text-gray-400">orders</div>
            </div>
          </div>
        </div>
      </div>

      {/* Stage drill-down */}
      {selectedStage && (
        <div className="bg-white rounded-xl shadow mb-6 overflow-hidden">
          <div className={`flex items-center justify-between px-4 py-3 ${selectedStage.light || 'bg-gray-50'} border-b`}>
            <div>
              <h2 className="font-semibold text-gray-800">{selectedStage.key === '__all__' ? 'All Orders' : selectedStage.key}</h2>
              <p className="text-xs text-gray-500">{stageOrders.length} work order{stageOrders.length !== 1 ? 's' : ''}</p>
            </div>
            <button onClick={() => { setSelectedStage(null); setStageOrders([]); }} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
          </div>
          {stageLoading ? (
            <div className="p-8 text-center text-gray-400">Loading...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-3 font-medium text-gray-600">WO #</th>
                    <th className="text-left p-3 font-medium text-gray-600">Stop Location</th>
                    <th className="text-left p-3 font-medium text-gray-600">Status</th>
                    <th className="text-left p-3 font-medium text-gray-600">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {stageOrders.map((wo, i) => (
                    <tr
                      key={wo.id || i}
                      className="border-b hover:bg-blue-50 cursor-pointer transition-colors"
                      onClick={() => wo.cause_id && navigate(`/causes/${wo.cause_id}`, { state: { tab: 2 } })}
                    >
                      <td className="p-3 font-medium text-blue-700 underline decoration-dotted">{wo.wo_number}</td>
                      <td className="p-3 text-gray-600 max-w-[200px] truncate">{wo.stop_location_name || '-'}</td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[wo.status] || 'bg-gray-100 text-gray-700'}`}>{wo.status}</span>
                      </td>
                      <td className="p-3 text-gray-500">{(wo.created_at || '').split('T')[0]}</td>
                    </tr>
                  ))}
                  {stageOrders.length === 0 && (
                    <tr><td colSpan={4} className="p-6 text-center text-gray-400">No orders in this stage</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-800">Recent Activity</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left p-3 font-medium text-gray-600">WO #</th>
                <th className="text-left p-3 font-medium text-gray-600">Patient</th>
                <th className="text-left p-3 font-medium text-gray-600">Stop Location</th>
                <th className="text-left p-3 font-medium text-gray-600">Status</th>
                <th className="text-left p-3 font-medium text-gray-600">Date</th>
              </tr>
            </thead>
            <tbody>
              {(stats?.recent_activity || []).map((a, i) => (
                <tr
                  key={i}
                  className="border-b hover:bg-blue-50 cursor-pointer transition-colors"
                  onClick={() => a.cause_id && navigate(`/causes/${a.cause_id}`, { state: { tab: 2 } })}
                  title={a.cause_id ? `Open case ${a.cause_number || a.cause_id}` : ''}
                >
                  <td className="p-3 font-medium text-blue-700 underline decoration-dotted">{a.wo_number}</td>
                  <td className="p-3">{a.patient_name}</td>
                  <td className="p-3">{a.stop_location}</td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[a.status] || 'bg-gray-100'}`}>{a.status}</span>
                  </td>
                  <td className="p-3 text-gray-500">{a.created_at?.split('T')[0]}</td>
                </tr>
              ))}
              {(!stats?.recent_activity || stats.recent_activity.length === 0) && (
                <tr><td colSpan={5} className="p-4 text-center text-gray-400">No recent activity</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (!token || !userData) {
      navigate('/login');
      return;
    }
    setUser(JSON.parse(userData));
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  if (!user) return null;

  const isActive = (path) => location.pathname === path;

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: '\u{1F3E0}' },
    { path: '/search', label: 'Search Orders', icon: '\u{1F4CB}' },
    { path: '/causes/new', label: 'New Order', icon: '\u2795' },
  ];

  if (user.role === 'admin') {
    navItems.push({ path: '/templates', label: 'Templates', icon: '📋' });
  }

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-20 md:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed md:static inset-y-0 left-0 z-30 w-64 transform transition-transform md:transform-none ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        }`}
        style={{ backgroundColor: '#1a2744' }}
      >
        <div className="flex flex-col h-full">
          <div className="p-5 border-b border-blue-800">
            <h2 className="text-lg font-bold text-white">Discovery Records</h2>
            <p className="text-xs text-blue-300">Records Management System</p>
          </div>

          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive(item.path)
                    ? 'bg-blue-800 text-white'
                    : 'text-blue-200 hover:bg-blue-800/50 hover:text-white'
                }`}
              >
                <span className="text-lg">{item.icon}</span>
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="p-4 border-t border-blue-800">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-full bg-blue-700 flex items-center justify-center text-white text-sm font-bold">
                {user.full_name.charAt(0)}
              </div>
              <div>
                <p className="text-sm text-white font-medium">{user.full_name}</p>
                <p className="text-xs text-blue-300 capitalize">{user.role}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full px-3 py-2 text-sm text-blue-200 hover:text-white hover:bg-blue-800/50 rounded-md transition-colors text-left"
            >
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white shadow-sm px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(true)}
            className="md:hidden text-gray-600 hover:text-gray-800"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div className="text-sm text-gray-500">
            Discovery Records - {user.role === 'admin' ? 'Administrator' : 'Employee'}
          </div>
        </header>

        <main className="flex-1 p-6">
          {children || <DashboardHome user={user} />}
        </main>
      </div>
    </div>
  );
}
